/**
 * 產品參數版本（批151 C2）——後台表單的純函式：宣告預檢、版本表的值、台北時間、錯誤訊息。
 *
 * ## 權威在後端
 *
 * 宣告的唯一解讀者是後端 `services/versioned_params.py`，版本規則在 `services/product_params.py`。
 * 本檔的 `validateDeclarationRows` 是宣告驗證的**逐條鏡像**（同樣的 `field` 路徑與訊息），只為了
 * 讓紅字在按下儲存前就出現；版本值的格式走 `lib/decimalStr.ts`（同樣是後端的鏡像）。
 *
 * ## 時間一律台北時間
 *
 * 生效時間在協定上是 UTC（`effective_from`）；後台的顯示與輸入一律是**台北時間**（UTC+8，
 * 無日光節約），不跟著瀏覽器時區走——同一個版本在每個人的畫面上都是同一個時刻。輸入框是
 * `datetime-local`，送出時補上 `+08:00`。
 */

import type {
  ParamBackfill,
  ParamDeclaration,
  ParamKeyDecl,
  ParamVersion,
  ParamVersionInput,
  ParamVersionState,
} from '@/api/productParams'

import { canonicalDecimal, DECIMAL_HINT } from './decimalStr'

// ---------------------------------------------------------------------------
// 常數（與後端 `services/versioned_params.py` 逐項相同）
// ---------------------------------------------------------------------------

/** `products.options` 裡的宣告鍵。 */
export const PARAMS_OPTION_KEY = 'versioned_params'
/** 「已用到第幾版」的內部計數鍵（後端專用；後台不顯示、不回存）。 */
export const PARAMS_SEQ_OPTION_KEY = 'versioned_params_seq'
export const DECLARATION_VERSION = 1
export const MAX_KEYS = 64
export const LABEL_MAX = 60
export const UNIT_MAX = 60
export const HINT_MAX = 200
export const NOTE_MAX = 500
const KEY_RE = /^[a-z][a-z0-9_]{0,39}$/

export const STATE_LABELS: Record<ParamVersionState, string> = {
  current: '目前生效',
  future: '未來',
  past: '已被取代',
}

// ---------------------------------------------------------------------------
// 宣告
// ---------------------------------------------------------------------------

/** 宣告表單的一列（字串欄一律存字串；`isNew`＝這次才新增的鍵，代號可改）。 */
export interface DeclRow {
  key: string
  label: string
  unit: string
  hint: string
  isNew: boolean
}

export interface ParamIssue {
  /** 後端同一套路徑：`keys[i].key`、`keys[i].label`…；版本值＝參數鍵本身；根＝空字串。 */
  field: string
  message: string
}

/** 既有宣告 → 表單列。 */
export function rowsFromDeclaration(decl: ParamDeclaration | null | undefined): DeclRow[] {
  return (decl?.keys ?? []).map((k) => ({
    key: k.key,
    label: k.label,
    unit: k.unit ?? '',
    hint: k.hint ?? '',
    isNew: false,
  }))
}

/** 整列空白＝多按了一次「新增」，不算資料。 */
function blankRow(r: DeclRow): boolean {
  return !r.key.trim() && !r.label.trim() && !r.unit.trim() && !r.hint.trim()
}

/** 表單列 → 送給後端的宣告（去頭尾空白、空字串的選填欄 → null；整列空白略過）。 */
export function declarationFromRows(rows: DeclRow[]): ParamDeclaration {
  const keys: ParamKeyDecl[] = rows
    .filter((r) => !blankRow(r))
    .map((r) => ({
      key: r.key.trim(),
      label: r.label.trim(),
      unit: r.unit.trim() || null,
      hint: r.hint.trim() || null,
    }))
  return { version: DECLARATION_VERSION, keys }
}

/** 以 code point 計長度（後端 `len()` 的語意）。 */
function cpLen(s: string): number {
  return [...s].length
}

/**
 * 宣告預檢（後端 `parse_declaration` 的鏡像，回**第一個**錯誤）。`field` 的索引是
 * 「去掉整列空白之後」的位置——與實際送出的陣列一致。
 */
export function validateDeclaration(decl: ParamDeclaration): ParamIssue | null {
  if (decl.keys.length > MAX_KEYS) {
    return { field: 'keys', message: `最多 ${MAX_KEYS} 個參數鍵，收到 ${decl.keys.length} 個` }
  }
  const seen = new Set<string>()
  for (const [i, k] of decl.keys.entries()) {
    const f = `keys[${i}]`
    if (!KEY_RE.test(k.key)) {
      return { field: `${f}.key`, message: '參數鍵只能用小寫英數與底線、字母開頭、最長 40 字' }
    }
    if (!k.label) return { field: `${f}.label`, message: '不可為空' }
    if (cpLen(k.label) > LABEL_MAX) return { field: `${f}.label`, message: `最長 ${LABEL_MAX} 字` }
    if (k.unit != null && cpLen(k.unit) > UNIT_MAX) return { field: `${f}.unit`, message: `最長 ${UNIT_MAX} 字` }
    if (k.hint != null && cpLen(k.hint) > HINT_MAX) return { field: `${f}.hint`, message: `最長 ${HINT_MAX} 字` }
    if (seen.has(k.key)) return { field: `${f}.key`, message: `參數鍵「${k.key}」重複` }
    seen.add(k.key)
  }
  return null
}

/** 表單列的預檢（先轉成要送的宣告，再走與後端相同的檢查）。 */
export function validateDeclarationRows(rows: DeclRow[]): ParamIssue | null {
  return validateDeclaration(declarationFromRows(rows))
}

/** 錯誤點名的是不是「去掉空白列之後的第 i 列」的某一格。 */
export function declIssueHits(issue: ParamIssue | null, index: number, cell: keyof ParamKeyDecl): boolean {
  return !!issue && issue.field === `keys[${index}].${cell}`
}

/** 表單列 index → 送出陣列的 index（空白列不算；空白列本身回 -1）。 */
export function sentIndex(rows: DeclRow[], index: number): number {
  if (blankRow(rows[index])) return -1
  return rows.slice(0, index).filter((r) => !blankRow(r)).length
}

// ---------------------------------------------------------------------------
// 版本
// ---------------------------------------------------------------------------

/** 一個鍵的欄頭（`名稱（單位）`）。 */
export function keyHeading(k: ParamKeyDecl): string {
  return k.unit ? `${k.label}（${k.unit}）` : k.label
}

/**
 * 新增版本時預帶的值＝「上一版」（`effective_from` 最晚的那一版，含未來版本）的值；
 * 沒有任何版本就全空。只帶宣告裡有的鍵（宣告已移除的舊鍵不帶）。
 */
export function seedValues(decl: ParamDeclaration, versions: ParamVersion[]): Record<string, string> {
  const latest = [...versions].sort((a, b) =>
    a.effective_from === b.effective_from
      ? versionNo(b.version_id) - versionNo(a.version_id)
      : a.effective_from < b.effective_from
        ? 1
        : -1,
  )[0]
  const out: Record<string, string> = {}
  for (const k of decl.keys) out[k.key] = latest?.values?.[k.key] ?? ''
  return out
}

function versionNo(id: string): number {
  const m = /^v(\d+)$/.exec(id)
  return m ? Number(m[1]) : 0
}

/**
 * 版本值的預檢：每個宣告鍵都要有值、值是合法十進位字串。回 `{鍵: 錯誤訊息}`（空物件＝通過）
 * 與**標準形**的值（送出用；後端也會再正規化一次）。
 */
export function checkValues(
  decl: ParamDeclaration,
  values: Record<string, string>,
): { errors: Record<string, string>; clean: Record<string, string> } {
  const errors: Record<string, string> = {}
  const clean: Record<string, string> = {}
  for (const k of decl.keys) {
    const raw = (values[k.key] ?? '').trim()
    if (!raw) {
      errors[k.key] = '必填'
      continue
    }
    const c = canonicalDecimal(raw)
    if (c == null) errors[k.key] = DECIMAL_HINT
    else clean[k.key] = c
  }
  return { errors, clean }
}

/** 尚未生效的版本有幾版（新增鈕的上限提示用）。 */
export function futureCount(versions: ParamVersion[]): number {
  return versions.filter((v) => v.state === 'future').length
}

// ---------------------------------------------------------------------------
// 台北時間
// ---------------------------------------------------------------------------

const TAIPEI_OFFSET_MS = 8 * 3600 * 1000
/**
 * `datetime-local` 的值：秒可有可無（`step="1"` 時才會有）；小數秒也收但捨棄——協定以秒為單位，
 * 而有的實作會把 `:37` 正規化成 `:37.000`。
 */
const INPUT_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?$/

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** 時刻 → 台北時間的各欄位（用 UTC getter 讀「加了 8 小時」的 Date，不依賴瀏覽器時區）。 */
function taipeiParts(ms: number) {
  const d = new Date(ms + TAIPEI_OFFSET_MS)
  return {
    y: d.getUTCFullYear(),
    mo: d.getUTCMonth() + 1,
    d: d.getUTCDate(),
    h: d.getUTCHours(),
    mi: d.getUTCMinutes(),
    s: d.getUTCSeconds(),
  }
}

/** ISO → `2026/11/01 00:00`（台北時間；秒不為 0 時帶秒）。壞值原樣回。 */
export function fmtTaipei(iso: string | null | undefined): string {
  if (!iso) return '—'
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return iso
  const p = taipeiParts(ms)
  const base = `${p.y}/${pad2(p.mo)}/${pad2(p.d)} ${pad2(p.h)}:${pad2(p.mi)}`
  return p.s ? `${base}:${pad2(p.s)}` : base
}

/** 時刻 → `datetime-local` 的值（台北時間 `YYYY-MM-DDTHH:mm`；`withSeconds` 且秒不為 0 時帶 `:ss`）。 */
function inputFromMs(ms: number, withSeconds: boolean): string {
  const p = taipeiParts(ms)
  const base = `${p.y}-${pad2(p.mo)}-${pad2(p.d)}T${pad2(p.h)}:${pad2(p.mi)}`
  return withSeconds && p.s ? `${base}:${pad2(p.s)}` : base
}

/**
 * ISO → `datetime-local` 的值（台北時間 `YYYY-MM-DDTHH:mm`，**秒不為 0 時帶 `:ss`**）。
 *
 * ★ 批151 R3-16：原本一律截到分——修改一個生效時間帶秒的未生效版本（API／MCP 建的、或立即生效
 *   往後挪過一秒的），什麼都沒動按儲存，生效時間就被悄悄改早了幾十秒。輸入框要配 `step="1"`
 *   才顯示得出秒（也才不會把帶秒的值判成不合 step）。
 */
export function taipeiInputFromIso(iso: string): string {
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return ''
  return inputFromMs(ms, true)
}

/** `datetime-local` 的值（視為台北時間）→ 帶時區的 ISO；格式不對回 null。 */
export function isoFromTaipeiInput(value: string): string | null {
  const m = INPUT_RE.exec(value.trim())
  if (!m) return null
  const [, y, mo, d, h, mi, s] = m
  const iso = `${y}-${mo}-${d}T${h}:${mi}:${s ?? '00'}+08:00`
  return Number.isNaN(Date.parse(iso)) ? null : iso
}

/** 新增版本的預設生效時間＝台北時間的**明天 00:00**。 */
export function defaultEffectiveInput(nowMs: number = Date.now()): string {
  const p = taipeiParts(nowMs + 24 * 3600 * 1000)
  return `${p.y}-${pad2(p.mo)}-${pad2(p.d)}T00:00`
}

/** `datetime-local` 的 `min`＝台北時間的現在（到分；真正的「不可早於現在」由 `effectiveError` 以秒判）。 */
export function minEffectiveInput(nowMs: number = Date.now()): string {
  return inputFromMs(nowMs, false)
}

/**
 * 新增／修改版本的 body。`immediate`＝立即生效（**只用於新增**）：不送 `effective_from`，
 * 由伺服器取現在時刻。其餘情況把台北時間的輸入轉成帶 `+08:00` 的 ISO；格式不對回 null。
 */
export function versionBody(opts: {
  immediate: boolean
  effective: string
  values: Record<string, string>
  note: string
}): ParamVersionInput | null {
  const note = opts.note.trim() || null
  if (opts.immediate) return { values: opts.values, note }
  const iso = isoFromTaipeiInput(opts.effective)
  return iso ? { effective_from: iso, values: opts.values, note } : null
}

/** 生效時間的預檢：格式、不可早於現在（後端以秒比；這裡只是先擋一手）。 */
export function effectiveError(value: string, nowMs: number = Date.now()): string | null {
  const iso = isoFromTaipeiInput(value)
  if (!iso) return '請選擇生效時間'
  if (Date.parse(iso) < Math.floor(nowMs / 1000) * 1000) return '生效時間不可早於現在（已生效的版本不可補建）'
  return null
}

// ---------------------------------------------------------------------------
// 錯誤訊息與結果摘要
// ---------------------------------------------------------------------------

/**
 * 錯誤碼的中文說明。後端的 `message` 本身就是中文且更具體（會點名是哪個鍵、哪一版），
 * 所以**有 `message` 一律用它**；這張表是 message 缺席時（或不是這組 API 的錯誤形狀）的兜底。
 */
export const PARAM_ERROR_LABELS: Record<string, string> = {
  invalid_type: '欄位型別不正確',
  unknown_key: '有不認得的欄位或參數鍵',
  missing_key: '缺少必填的欄位或參數鍵',
  invalid_value: '欄位的值不合格式',
  invalid_effective_from: '生效時間格式不正確',
  effective_from_in_past: '生效時間不可早於現在（已生效的版本不可補建）',
  effective_from_taken: '同一個生效時間已有版本（同一時間點不可有兩版）',
  too_many_future_versions: '尚未生效的版本已達上限，請先刪除或等待既有版本生效',
  version_effective: '這一版已生效，不可修改或刪除（要調整請新增一版）',
  version_not_found: '找不到這一版，可能已被刪除，請重新整理',
  version_conflict: '版本號或生效時間與既有版本衝突，請重新整理後再試',
  versioned_params_not_declared: '這個產品還沒有宣告參數鍵，請先設定參數宣告',
  initial_values_required: '新增的參數鍵要同時提供初始值',
  current_version_invalid: '目前生效的版本資料損壞，無法自動補版',
  invalid_note: '備註格式不正確',
  product_not_found: '找不到這個產品',
}

/** 後端 422／409 的 `detail` 取中文訊息（`message` 優先、錯誤碼兜底）。 */
export function paramErrorMessage(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') return detail
  if (detail && typeof detail === 'object') {
    const d = detail as { message?: unknown; code?: unknown }
    if (typeof d.message === 'string' && d.message) return d.message
    if (typeof d.code === 'string' && PARAM_ERROR_LABELS[d.code]) return PARAM_ERROR_LABELS[d.code]
  }
  return fallback
}

/** `detail.field`（點名的欄位；沒有回空字串）。 */
export function paramErrorField(detail: unknown): string {
  if (detail && typeof detail === 'object') {
    const f = (detail as { field?: unknown }).field
    if (typeof f === 'string') return f
  }
  return ''
}

/** 是不是「新增的鍵要初始值」——是的話回缺的鍵，否則回 null。 */
export function missingInitialKeys(detail: unknown): string[] | null {
  if (!detail || typeof detail !== 'object') return null
  const d = detail as { code?: unknown; missing_keys?: unknown }
  if (d.code !== 'initial_values_required' || !Array.isArray(d.missing_keys)) return null
  return d.missing_keys.filter((k): k is string => typeof k === 'string')
}

/** 宣告存檔後的提示句（裁示 121 的自動補版結果）。 */
export function backfillSummary(b: ParamBackfill | null | undefined): string {
  const bits: string[] = []
  if (b?.created_version_id) bits.push(`已自動補一版 ${b.created_version_id}（立即生效）`)
  const n = b?.patched_version_ids?.length ?? 0
  if (n) bits.push(`更新 ${n} 個未來版本（${b!.patched_version_ids.join('、')}）`)
  if (!bits.length) return '已儲存參數宣告。'
  return `已儲存參數宣告：${bits.join('、')}。`
}
